import React, { useRef } from "react";
import { Box, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, FormHelperText, IconButton, InputAdornment, InputLabel, Link, Stack, TextareaAutosize, Typography } from "@mui/material";
import { AlternateEmail, Close, ContactMail, Info, IosShare, Send, TextSnippet } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { updateStateData } from "../../../../Store/actions";
import TextInputField from "../../../../components/commonComponents/TextInputs/TextInputField";
import CloseButton from "../../../../components/commonComponents/Buttons/CloseButton";
import ActionButton, { ActionButtonWithToolTip, DarkGreyButton } from "../../../../components/commonComponents/Buttons/ActionButton";
import { HtmlTooltip, StyledBlackInformationTypography, StyledSwitch } from "../../../../components/commonComponents/StyledComponents/styled";
import { sendReportShareInfo } from "../../../../api_calls/campaigns";
import EmailSelection from "./EmailSelection";
import { resetShareModalInfo, sendTestEmailToSelf, shareReport, validateReportShareInfo } from "../../../../api_calls/ReportSettings";
import ReactQuill from "react-quill";
import { DisabledGreyButton } from "../../../../components/commonComponents/Buttons/DisabledButton";
import Confirmation from "../../../../components/commonComponents/Confirmation";
import { toast } from "react-toastify";

const ShareModal = ({ open, onClose, size, usedFor, closeModal, campaignRecord }) => {
    const currentStateData = useSelector((state) => state.userSelections);
    const dispatchAction = useDispatch();
    const quillRef = useRef(null);
    const [value, setValue] = React.useState(currentStateData?.personalised_message);
    const [fieldErrors, setFieldErrors] = React.useState({});
    const [showConfirmation, setShowConfirmation] = React.useState(false);
    const [showCancelConfirmation, setShowCancelConfirmation] = React.useState(false);

    const validateInvitation = () => {
        setFieldErrors({});
        dispatchAction(
            updateStateData({
                personalised_message: value,
            })
        );
        const validateData = validateReportShareInfo(campaignRecord.campaign_id, false);
        if (validateData.error === true) {
            toast("Please fill in valid information", { type: "error" });
            setFieldErrors(validateData.errorMessages);
        } else {
            setShowConfirmation(true);
        }
    }

    const sendInvite = (sendToSelf) => {
        shareReport(campaignRecord.campaign_id, sendToSelf).then((info) => {
            if (info.success === false) {
                setFieldErrors(info.errorMessages);
            } else {
                closeModal();
            }
        });
    }

    const sendTestEmail = () => {
        dispatchAction(
            updateStateData({
                personalised_message: value,
            })
        );
        sendTestEmailToSelf(campaignRecord.campaign_id);
    }

    return (
        <Dialog
            open={open}
            onClose={(e) => {
                e.preventDefault();
            }}
            fullWidth={true}
            maxWidth={size}
        >
            <DialogTitle> Share Report </DialogTitle>
            <DialogContent>
                {/* ------------ info text ----------- */}
                <StyledBlackInformationTypography variant="body2" sx={{ marginLeft: 0, fontWeight: "normal" }}>
                    Enter all e-mail addresses or select from your list of favorites to share the campaign report with. You can also choose whether the brand should be notified about the report. If desired, you can include a custom message for the notification e-mail.
                </StyledBlackInformationTypography>
                <Box height={5} />
                <StyledBlackInformationTypography variant="body2" sx={{ marginLeft: 0, fontWeight: "normal" }}>
                    When an e-mail address is invited for the first time, an automated invitation to create a CAP Explorer account will always be sent, regardless of the notification settings.
                </StyledBlackInformationTypography>
                <Box height={15} />
                {/* ------- email selection -------- */}
                <EmailSelection fieldErrors={fieldErrors} />
                {/* --------- boolean switches to send email notification -------- */}
                <Box height={15} />
                <FormControlLabel
                    control={
                        <StyledSwitch
                            checked={typeof currentStateData.send_email_notif !== "undefined" ? currentStateData.send_email_notif : false}
                            name="new_shared_campaign_notification"
                            onChange={(e) => {
                                let booleanStates = {
                                    send_email_notif: e.target.checked,
                                    show_report_cancel_confirmation: true
                                }
                                if (e.target.checked === false) {
                                    booleanStates = {
                                        ...booleanStates,
                                        send_personal_message: false,
                                    }
                                }
                                dispatchAction(updateStateData(booleanStates));
                            }}
                        />}
                    labelPlacement="right"
                    label={"Send an e-mail notification about newly shared campaign"}
                    sx={{ fontSize: "12px", minWidth: 100 }}
                />
                <Box height={5} />
                <FormControlLabel
                    control={
                        <StyledSwitch
                            disabled={typeof currentStateData.send_email_notif !== "undefined" ? !currentStateData.send_email_notif : true}
                            checked={typeof currentStateData.send_personal_message !== "undefined" ? currentStateData.send_personal_message : false}
                            name="personalized_notification"
                            onChange={(e) => {
                                dispatchAction(
                                    updateStateData({
                                        send_personal_message: e.target.checked,
                                    })
                                );
                            }}
                        />}
                    labelPlacement="right"
                    label={"Send personalized message"}
                    sx={{ fontSize: "12px", minWidth: 100 }}
                />
                <Box height={10} />
                {/* ------------- personalized message box ------------ */}
                {
                    typeof currentStateData.send_personal_message !== "undefined" && currentStateData.send_personal_message === true && (
                        <>
                            <InputLabel shrink sx={{ color: "#AF3241" }}>Your Message*</InputLabel>
                            <ReactQuill
                                ref={quillRef}
                                theme="snow"
                                value={value}
                                onChange={setValue}
                                readOnly={typeof currentStateData.send_personal_message !== "undefined" ? !currentStateData.send_personal_message : false}
                                modules={{
                                    toolbar: [
                                        [{ header: [1, 2, false] }],
                                        ["bold", "italic", "underline", "strike"],
                                        [
                                            { list: "bullet" },
                                            // { indent: "-1" },
                                            // { indent: "+1" }
                                        ],
                                        ["clean"],
                                    ],
                                }}
                            />
                            {typeof fieldErrors.personal_message !== "undefined" && (
                                <FormHelperText sx={{ color: "#AF3241" }}>{fieldErrors.personal_message}</FormHelperText>
                            )}
                        </>
                    )
                }



            </DialogContent>
            <Box height={20} />
            <DialogActions>
                <Stack direction={"row"} spacing={2}>
                    <CloseButton
                        onClick={(e) => {
                            e.preventDefault();
                            if (typeof currentStateData.show_report_cancel_confirmation !== "undefined" && currentStateData.show_report_cancel_confirmation === true) {
                                setShowCancelConfirmation(true);
                            } else {
                                onClose(false);
                            }

                        }}
                        label="Cancel"
                        icon={<Close />}
                    />
                    <ActionButtonWithToolTip
                        onClick={(e) => {
                            e.preventDefault();
                            setFieldErrors({});
                            sendTestEmail();
                        }}
                        label={"Test E-Mail"}
                        icon={<AlternateEmail />}
                        toolTipContent="Send a test email to yourself"
                    />

                    <ActionButton
                        onClick={(e) => {
                            e.preventDefault();
                            setFieldErrors({});
                            validateInvitation();
                        }}
                        label={"Share"}
                        icon={<IosShare />}
                    />
                </Stack>
                {showConfirmation === true && (
                    <Confirmation
                        confirmationActionCall={() => {
                            sendInvite(false);
                        }}
                        closePopup={(e) => {
                            setShowConfirmation(false);
                        }}
                        showConfirmationPopup={showConfirmation}
                        confirmationTitle={"Confirm Action"}
                        confirmationMessage={
                            <>
                                <Typography variant="body2">
                                    You are about to share reports with external parties.
                                </Typography>
                                <Box height={20} />
                                <Typography variant="body2">Please confirm that all information is correct before proceeding.</Typography>
                            </>
                        }
                    />
                )}
                {showCancelConfirmation === true && (
                    <Confirmation
                        confirmationActionCall={() => {
                            onClose(false);
                        }}
                        closePopup={(e) => {
                            setShowCancelConfirmation(false);
                        }}
                        showConfirmationPopup={showCancelConfirmation}
                        confirmationTitle={"Confirm Action"}
                        confirmationMessage={
                            <>
                                <Typography variant="body2">
                                    Are you certain you want to discard all the changes you have made?
                                </Typography>
                                {/* <Box height={20} />
                                <Typography variant="body2">Please confirm that all information is correct before proceeding.</Typography> */}
                            </>
                        }
                    />
                )}
            </DialogActions>
        </Dialog>
    );
};

export default ShareModal;
